import { ArrowLeft } from '@/assets/icons/arrow-left';
import { ArrowRight } from '@/assets/icons/arrow-right';
import Button from '@/components/basicComponents/button';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import {
  DateContent,
  DollarContent,
  JsonContent,
  LongFormContent,
  NumberContent,
  PercentageContent,
  ShortTextContent
} from '@/features/fieldEditorModalPages/components/contents/index';
import { useAuth } from '@/hooks/use-auth';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { IFieldEditorPromptGroup } from '@/shared/types/fieldEditor';
import { FieldConfigurationModalPage } from '@/shared/ui/fieldConfigurationModalPage';
import { FieldPromptEditorModalPage } from '@/shared/ui/fieldPromptEditorModalPage';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { AIFieldType } from '@trace-8/trace8-schema';
import styled from 'styled-components';
import { useFieldEditorContext } from '../context/FieldEditorContext';

type FieldEditorModalPageType = 'default' | 'fieldConfiguration' | 'promptEditor';

export const FieldEditorRenderer = () => {
  const { user } = useAuth();
  const { searchParams, setSearchParams } = useSearchParamsManager();
  const { allFields, currentField, ids, editFieldPageType, setIsAiPaneOpen, setCurrentField, setEditFieldPageType, mainQueryRefetch, handleClose } =
    useFieldEditorContext();

  const fieldEditorModalPageType: FieldEditorModalPageType = (searchParams.get('fieldEditorModalPageType') as FieldEditorModalPageType) || 'default';

  const prevField = () => {
    if (!allFields || !currentField) return;
    const currentIndex = allFields.findIndex((field) => field.id === currentField.id);

    if (currentIndex > 0) {
      setCurrentField(allFields[currentIndex - 1]);
      setEditFieldPageType(allFields[currentIndex - 1]?.documentFieldType ?? 'default');
    }
    mainQueryRefetch();
  };

  const nextField = () => {
    if (!allFields || !currentField) return;
    const currentIndex = allFields.findIndex((field) => field.id === currentField.id);

    if (currentIndex !== -1 && currentIndex < allFields.length - 1) {
      setCurrentField(allFields[currentIndex + 1]);
      setEditFieldPageType(allFields[currentIndex + 1]?.documentFieldType ?? 'default');
    }
    mainQueryRefetch();
  };

  const savePromptGroup = (promptGroup: IFieldEditorPromptGroup) => {
    const updatedCurrentField = {
      ...currentField,
      promptGroup: promptGroup
    };
    setCurrentField(updatedCurrentField);
  };

  const handleFieldEditorModalPageType = (type: FieldEditorModalPageType) => {
    setSearchParams(
      (prev) => {
        if (type === 'default') {
          prev.delete('fieldEditorModalPageType');
        } else {
          prev.set('fieldEditorModalPageType', type);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const handleCurrentFieldChange = (id: string) => {
    const selectedField = allFields?.find((field) => field.id === id);
    if (selectedField) {
      setCurrentField(selectedField);
      setEditFieldPageType(selectedField?.documentFieldType || 'default');
    }
  };

  if (!currentField) return <></>;

  return (
    <>
      {fieldEditorModalPageType === 'fieldConfiguration' && (
        <FieldConfigurationModalPage
          allFields={allFields}
          currentField={currentField}
          ids={ids}
          handleCurrentFieldChange={handleCurrentFieldChange}
          openPromptEditorModalPage={() => handleFieldEditorModalPageType('promptEditor')}
          handleClose={() => handleFieldEditorModalPageType('default')}
        />
      )}
      {fieldEditorModalPageType === 'promptEditor' && (
        <FieldPromptEditorModalPage
          currentRow={currentField}
          currentRowName={currentField?.name ?? ''}
          isDueDiligenceFunnelTierSectionField={Boolean(ids?.funnelId && ids?.funnelStaticId)}
          refetch={mainQueryRefetch}
          savePromptGroup={savePromptGroup}
          handleClose={() => handleFieldEditorModalPageType('fieldConfiguration')}
        />
      )}
      {fieldEditorModalPageType === 'default' && (
        <>
          <MainWrap>
            <Header
              aiButtonConfig={{
                onClick: () => setIsAiPaneOpen((prev) => !prev)
              }}
              modalControl={
                <HeaderButtonsWrapper>
                  <GoBackButton handleClose={handleClose} backToTitle="" />
                  <NavigateButtonsWrapper>
                    <LeftBtn disabled={Boolean(allFields && allFields[0].id === currentField.id)} onClick={prevField}>
                      <ArrowLeft width={18} height={18} />
                    </LeftBtn>
                    <RightBtn disabled={Boolean(allFields && allFields[allFields.length - 1].id === currentField.id)} onClick={nextField}>
                      <ArrowRight width={18} height={18} />
                    </RightBtn>
                  </NavigateButtonsWrapper>
                </HeaderButtonsWrapper>
              }
            />
            <CustomPageTitleWrap withBorder>
              {currentField.name}
              {user && user.roles.includes('Super Admin') && (
                <Button size="md" styleType="outline" onClick={() => handleFieldEditorModalPageType('fieldConfiguration')}>
                  Configure Field
                </Button>
              )}
            </CustomPageTitleWrap>
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT) && <LongFormContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT) && <ShortTextContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.DATE) && <DateContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER) && <NumberContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR) && <DollarContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE) && <PercentageContent />}
            {editFieldPageType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON) && <JsonContent />}
          </MainWrap>
        </>
      )}
    </>
  );
};

export default FieldEditorRenderer;

const HeaderButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const NavigateButtonsWrapper = styled.div`
  display: flex;
  gap: 5px;
`;

const LeftBtn = styled.div<{ disabled: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ disabled }) => (disabled ? themeVar('action.disabled') : themeVar('layer.1'))};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ disabled }) => (disabled ? themeVar('font.disabled') : themeVar('font.action'))};
`;

const RightBtn = styled(LeftBtn)``;

const CustomPageTitleWrap = styled(PageTitleWrap)`
  justify-content: space-between;
`;
